import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useAppSelector } from '../../store/hooks';

interface CreateEditSchemaModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: SchemaFormData) => void;
  mode: 'create' | 'edit';
  saving?: boolean;
  saveError?: string | null;
}

export interface SchemaFormData {
  id?: string;
  schemaName: string;
  description: string;
}

const CreateEditSchemaModal: React.FC<CreateEditSchemaModalProps> = ({
  open,
  onClose,
  onSave,
  mode,
  saving = false,
  saveError = null,
}) => {
  const { selectedSchema, loading } = useAppSelector((state) => state.schemas);

  const [schemaName, setSchemaName] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (mode === 'edit' && selectedSchema) {
      setSchemaName(selectedSchema.schemaName);
      setDescription(selectedSchema.description);
    } else if (mode === 'create') {
      setSchemaName('');
      setDescription('');
    }
  }, [mode, selectedSchema, open]);

  const handleSave = () => {
    const formData: SchemaFormData = {
      schemaName,
      description,
    };

    if (mode === 'edit' && selectedSchema) {
      formData.id = selectedSchema.id;
    }

    onSave(formData);
  };

  const handleClose = () => {
    onClose();
  };

  const isFormValid = schemaName && description;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {mode === 'create' ? 'Crear Nuevo Esquema' : 'Editar Esquema'}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ mt: 2 }}>
            {saveError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {saveError}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Nombre del Esquema"
              value={schemaName}
              onChange={(e) => setSchemaName(e.target.value)}
              margin="normal"
              required
              disabled={saving}
              helperText="Nombre único para identificar el esquema"
            />

            <TextField
              fullWidth
              label="Descripción"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              margin="normal"
              multiline
              rows={3}
              required
              disabled={saving}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="outlined" disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || saving || !isFormValid}
        >
          {saving ? 'Guardando...' : mode === 'create' ? 'Crear' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateEditSchemaModal;
